import { Constant } from '@/modules/core/legacy/charts/am5/charts.constants';
import { XYChart, XYCursor } from '@amcharts/amcharts5/xy';
import { percent } from '@amcharts/amcharts5';
import { useBullets } from '@/modules/core/legacy/charts/am5/line/composables/useBullets';
import { GoalProgressBarOptions } from '@/modules/ta/goal/goal.constants';

export function useXYChart(context) {
  const { root, chart, config, isRotated } = context();

  function createXYChart() {
    const pinchOptions = isRotated.value
      ? {
          wheelX: config?.value?.disableZoom ? Constant.NONE : Constant.PAN_Y,
          wheelY: config?.value?.disableZoom ? Constant.NONE : Constant.ZOOM_Y,
          pinchZoomY: !config?.value?.disableZoom,
        }
      : {
          wheelX: config?.value?.disableZoom ? Constant.NONE : Constant.PAN_X,
          wheelY: config?.value?.disableZoom ? Constant.NONE : Constant.ZOOM_X,
          pinchZoomX: !config?.value?.disableZoom,
        };

    const goalProps = {};
    if (config.value?.isGoal) {
      goalProps.height = percent(90);
      goalProps.zoomX = false;
      goalProps.zoomY = false;
    }

    chart.value = root.value.container.children.push(
      XYChart.new(root.value, {
        layout: root.value.verticalLayout,
        panX: !config?.value?.disableZoom,
        panY: !config?.value?.disableZoom,
        ...goalProps,
        ...pinchOptions,
        maxTooltipDistance: -1,
      })
    );
  }

  function createXYCursor() {
    const cursor = chart.value.set(Constant.CURSOR, XYCursor.new(root.value, {}));
    cursor.lineX.set(Constant.VISIBLE, config.value.cursorEnabled);
    cursor.lineY.set(Constant.VISIBLE, config.value.cursorEnabled);
  }

  function handleLabelOverlapForXYChart() {
    const { sliceChartTemplate } = useBullets(context);
    if (!chart.value.series || !chart.value.series.values[0]) return;
    // Remove old event handlers first
    sliceChartTemplate.events.off(GoalProgressBarOptions.POSITION_CHANGED);

    sliceChartTemplate.events.on(GoalProgressBarOptions.POSITION_CHANGED, () => {
      processCollisions();
    });
  }

  /**
   * Handles label collision for all chart types.
   * Uses:
   *  - processLineCollisions() for line/mixed charts
   *  - processBarCollisions() for stacked/column charts
   */
  function processCollisions() {
    if (!chart?.value?.series?.values?.length) return;

    const hasColumnSeries = chart.value.series.values.some((series) => {
      const name = series.className || '';
      return name.includes('ColumnSeries') || !!series.columns;
    });

    if (hasColumnSeries) {
      processBarCollisions();
    } else {
      processLineCollisions();
    }
  }

  /**
   * 🟦 Handles generic label collision (for Line / Combo / Area)
   */
  function processLineCollisions() {
    const allSprites = [];

    chart.value.series.each((series) => {
      const userData = series.get(Constant.USER_DATA);
      if (!userData?.showMetricLabels) return;

      series.dataItems.forEach((dataItem) => {
        if (dataItem?.bullets?.length > 0) {
          const bullet = dataItem.bullets[1];
          const sprite = bullet?.get?.(Constant.SPRITE);
          if (sprite) {
            allSprites.push({
              sprite,
              x: sprite.x(),
              y: sprite.y(),
            });
          }
        }
      });
    });

    if (!allSprites.length) return;

    const xThreshold = 18;
    const yThreshold = 10;
    const ySpacing = 12;
    const xSpacing = 17;

    const plot = chart.value.plotContainer;
    const plotHeight = plot.height();
    const bottomSafeZone = Math.max(
      0,
      chart.value.xAxes?.values?.[0]?.get('renderer')?.labelsContainer?.height() || 0
    );

    plot.set('maskContent', false);

    allSprites.sort((a, b) => a.x - b.x);

    const xGroups = [];
    let currentGroup = [allSprites[0]];
    for (let i = 1; i < allSprites.length; i++) {
      const prev = allSprites[i - 1];
      const curr = allSprites[i];
      if (Math.abs(curr.x - prev.x) <= xThreshold) {
        currentGroup.push(curr);
      } else {
        xGroups.push(currentGroup);
        currentGroup = [curr];
      }
    }
    xGroups.push(currentGroup);

    xGroups.forEach((group) => {
      if (group.length <= 1) return;

      group.sort((a, b) => a.y - b.y);

      const clusters = [];
      let temp = [group[0]];
      for (let i = 1; i < group.length; i++) {
        const prev = group[i - 1];
        const curr = group[i];
        if (Math.abs(curr.y - prev.y) <= yThreshold) temp.push(curr);
        else {
          clusters.push(temp);
          temp = [curr];
        }
      }
      clusters.push(temp);

      clusters.forEach((cluster, ci) => {
        const n = cluster.length;
        if (n === 1) return;

        const startY = cluster[0].y - ((n - 1) / 2) * ySpacing;

        cluster.forEach((item, index) => {
          const targetY = startY + index * ySpacing;
          const dy = targetY - item.y;
          const dx = (index % 2 === 0 ? -1 : 1) * xSpacing * ci;

          const proposedY = item.y + dy;
          const minY = 0;
          const maxY = plotHeight - bottomSafeZone;
          const clampedY = Math.min(Math.max(proposedY, minY), maxY);
          const adjustedDy = clampedY - item.y;

          item.sprite.set('dy', adjustedDy);
          item.sprite.set('dx', dx);
        });
      });
    });
  }

  /**
   * 🟥 Handles label collision for stacked / column bar charts.
   * Moves labels outside if bar height too small.
   */
  function processBarCollisions() {
    const allSprites = [];
    const barRects = [];

    chart.value.series.each((series) => {
      const userData = series.get(Constant.USER_DATA);
      if (!userData?.showMetricLabels) return;

      const isColumn =
        series.columns || (series.className && series.className.includes('ColumnSeries'));

      series.dataItems.forEach((dataItem) => {
        if (dataItem?.bullets?.length > 0) {
          const bullet = dataItem.bullets[0];
          const sprite = bullet?.get?.(Constant.SPRITE);
          if (sprite) {
            allSprites.push({
              sprite,
              x: sprite.x(),
              y: sprite.y(),
              column: isColumn ? dataItem.get('graphics') : null,
            });
          }
        }

        if (isColumn) {
          const col = dataItem.get?.('graphics');
          if (col?.globalBounds) {
            const b = col.globalBounds();
            barRects.push({
              left: b.x,
              right: b.x + b.width,
              top: b.y,
              bottom: b.y + b.height,
              height: b.height,
            });
          }
        }
      });
    });

    if (!allSprites.length) return;

    // Config
    const minBarHeightForInsideLabel = 20;
    const offsetOutside = 10;
    const xThreshold = 18;
    const yThreshold = 10;
    const ySpacing = 12;
    const xSpacing = config.value.isLayered ? 10 : 0;

    const plot = chart.value.plotContainer;
    const plotHeight = plot.height();
    const bottomSafeZone = Math.max(
      7,
      chart.value.xAxes?.values?.[0]?.get('renderer')?.labelsContainer?.height() || 0
    );

    plot.set('maskContent', false);

    allSprites.sort((a, b) => a.x - b.x);

    const xGroups = [];
    let currentGroup = [allSprites[0]];
    for (let i = 1; i < allSprites.length; i++) {
      const prev = allSprites[i - 1];
      const curr = allSprites[i];
      if (Math.abs(curr.x - prev.x) <= xThreshold) currentGroup.push(curr);
      else {
        xGroups.push(currentGroup);
        currentGroup = [curr];
      }
    }
    xGroups.push(currentGroup);

    xGroups.forEach((group) => {
      group.sort((a, b) => a.y - b.y);

      const clusters = [];
      let temp = [group[0]];
      for (let i = 1; i < group.length; i++) {
        const prev = group[i - 1];
        const curr = group[i];
        if (Math.abs(curr.y - prev.y) <= yThreshold) temp.push(curr);
        else {
          clusters.push(temp);
          temp = [curr];
        }
      }
      clusters.push(temp);

      clusters.forEach((cluster, ci) => {
        const n = cluster.length;
        const startY = n > 1 ? cluster[0].y - ((n - 1) / 2) * ySpacing : cluster[0].y;

        cluster.forEach((item, index) => {
          let targetY = n > 1 ? startY + index * ySpacing : startY;
          let dy = targetY - item.y;
          const dx = (index % 2 === 0 ? -1 : 1) * xSpacing * ci;

          // Detect small bars & move outside
          if (item.column?.globalBounds) {
            const b = item.column.globalBounds();
            if (b.height < minBarHeightForInsideLabel) {
              const spaceAbove = b.y;
              const spaceBelow = plotHeight - (b.y + b.height);
              if (spaceAbove > spaceBelow) {
                dy = b.y - offsetOutside - item.y; // above
              } else {
                dy = b.y + b.height + offsetOutside - item.y; // below
              }
            }
          }

          // Clamp inside visible plot area
          const proposedY = item.y + dy;
          const minY = 0;
          const maxY = plotHeight - bottomSafeZone;
          const clampedY = Math.min(Math.max(proposedY, minY), maxY);
          const adjustedDy = clampedY - item.y;

          item.sprite.set('dy', adjustedDy);
          item.sprite.set('dx', dx);
        });
      });
    });
  }

  return {
    createXYChart,
    createXYCursor,
    handleLabelOverlapForXYChart,
  };
}
